import { useState, useEffect } from "react";
import { LanguageSelector } from "@/components/LanguageSelector";
import { TranslationPanel } from "@/components/TranslationPanel";
import { TranslationHistory } from "@/components/TranslationHistory";
import { ThemeToggle } from "@/components/ThemeToggle";
import { HelpDialog } from "@/components/HelpDialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { MessageSquare, Zap } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useTranslationHistory, type TranslationEntry } from "@/hooks/useTranslationHistory";
import { supabase } from "@/integrations/supabase/client";

export default function Translator() {
  const { toast } = useToast();
  const [sourceLanguage, setSourceLanguage] = useState("auto");
  const [targetLanguage, setTargetLanguage] = useState("en");
  const [sourceText, setSourceText] = useState("");
  const [translatedText, setTranslatedText] = useState("");
  const [isEditable, setIsEditable] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Use persistent history storage
  const {
    entries: translationEntries,
    addEntry,
    deleteEntry,
    deleteMultiple,
    toggleFavorite,
    updateEntry,
    searchHistory,
    exportHistory,
    importHistory,
  } = useTranslationHistory();

  // Enhanced Translation via Edge Function with robust error handling
  const translateText = async (text: string, from: string, to: string) => {
    setIsLoading(true);

    // Input validation and sanitization
    const sanitizedText = text.trim();
    if (!sanitizedText) {
      setIsLoading(false);
      return;
    }

    // Prevent duplicate translations
    const isDuplicate = translationEntries.some(
      (entry) => entry.sourceText === sanitizedText && entry.sourceLanguage === from && entry.targetLanguage === to,
    );

    if (isDuplicate) {
      const existingEntry = translationEntries.find(
        (entry) => entry.sourceText === sanitizedText && entry.sourceLanguage === from && entry.targetLanguage === to,
      );
      if (existingEntry) {
        setTranslatedText(existingEntry.translatedText);
        setIsLoading(false);
        toast({
          title: "✨ From history",
          description: "Retrieved existing translation",
        });
        return;
      }
    }

    // Use Edge Function to proxy requests to n8n (hides webhook URL from client)
    const API_ENDPOINT = "https://yqyibrhwvmcuxenyamrs.supabase.co/functions/v1/translate";
    const MAX_RETRIES = 3;
    const REQUEST_TIMEOUT = 30000; // 30 seconds

    let lastError: Error | null = null;

    // Get current session for authentication
    const { data: sessionData } = await supabase.auth.getSession();
    const accessToken = sessionData?.session?.access_token;

    for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
      try {
        // Create AbortController for timeout handling
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT);

        // Build headers - include auth token if available
        const headers: Record<string, string> = {
          "Content-Type": "application/json",
          "apikey": "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InlxeWlicmh3dm1jdXhlbnlhbXJzIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTMyMTEyNjAsImV4cCI6MjA2ODc4NzI2MH0.EtygFaAIcHr-TSPVgou-BC_zv9IGChNnNKlbePbDaLo",
        };
        
        if (accessToken) {
          headers["Authorization"] = `Bearer ${accessToken}`;
        }

        const response = await fetch(API_ENDPOINT, {
          method: "POST",
          headers,
          body: JSON.stringify({
            text: sanitizedText,
            sourceLang: from === "auto" ? "auto-detect" : from,
            targetLang: to,
            is_editable: isEditable,
          }),
          signal: controller.signal,
        });

        clearTimeout(timeoutId);

        // Enhanced HTTP status handling
        if (!response.ok) {
          let errorMessage = "Translation service unavailable";

          switch (response.status) {
            case 400:
              errorMessage = "Invalid translation request. Please check your text and languages.";
              break;
            case 401:
              errorMessage = "Authentication failed. Please try again.";
              break;
            case 403:
              errorMessage = "Access denied. Please check your permissions.";
              break;
            case 429:
              errorMessage = `Rate limit reached. Please wait ${attempt * 2} seconds and try again.`;
              if (attempt < MAX_RETRIES) {
                await new Promise((resolve) => setTimeout(resolve, attempt * 2000));
                continue;
              }
              break;
            case 500:
            case 502:
            case 503:
              errorMessage = `Server error (${response.status}). Retrying in ${attempt * 2} seconds...`;
              if (attempt < MAX_RETRIES) {
                await new Promise((resolve) => setTimeout(resolve, attempt * 2000));
                continue;
              }
              break;
            case 504:
              errorMessage = "Request timeout. The translation service is taking too long.";
              break;
            default:
              errorMessage = `Unexpected error (${response.status}). Please try again.`;
          }

          throw new Error(errorMessage);
        }

        // Robust response parsing
        let data;
        const contentType = response.headers.get("content-type");

        if (contentType && contentType.includes("application/json")) {
          try {
            data = await response.json();
          } catch (parseError) {
            throw new Error("Invalid response format from translation service");
          }
        } else {
          throw new Error("Translation service returned non-JSON response");
        }

        // Enhanced response validation
        if (!data) {
          throw new Error("Translation service returned empty response");
        }

        // Check for error response from edge function
        if (data.error) {
          throw new Error(data.error);
        }

        // Multiple fallback fields for different API response formats
        const translationResult =
          data.output ||
          data.translatedText ||
          data.result ||
          data.translation ||
          data.text ||
          (data.choices && data.choices[0]?.text) ||
          (data.data && data.data.translation);

        if (!translationResult || typeof translationResult !== "string") {
          throw new Error("Translation service did not return valid translation text");
        }

        const finalResult = translationResult.trim();
        if (!finalResult) {
          throw new Error("Translation service returned empty translation");
        }

        // Success: Add to history with persistent storage
        addEntry({
          sourceText: sanitizedText,
          translatedText: finalResult,
          sourceLanguage: from,
          targetLanguage: to,
          isFavorite: false,
        });
        setTranslatedText(finalResult);
        setIsLoading(false); // Critical: Reset loading state on success

        toast({
          title: "✨ Translation completed",
          description: `Successfully translated ${sanitizedText.length} characters`,
        });

        return; // Success - exit retry loop
      } catch (error) {
        lastError = error as Error;

        // Handle AbortError (timeout)
        if (error instanceof Error && error.name === "AbortError") {
          lastError = new Error("Translation request timed out. Please try again with shorter text.");
        }

        // Handle network errors
        if (error instanceof TypeError && error.message.includes("fetch")) {
          lastError = new Error("Network connection failed. Please check your internet connection.");
        }

        console.error(`Translation attempt ${attempt}/${MAX_RETRIES} failed:`, error);

        // If not the last attempt, wait before retrying
        if (attempt < MAX_RETRIES) {
          const delay = Math.min(1000 * Math.pow(2, attempt), 8000); // Exponential backoff, max 8s
          await new Promise((resolve) => setTimeout(resolve, delay));

          toast({
            title: `Retrying translation (${attempt}/${MAX_RETRIES})`,
            description: "Please wait...",
          });
        }
      }
    }

    // All retries failed
    setTranslatedText("");

    const errorMessage = lastError?.message || "Translation failed after multiple attempts";
    toast({
      title: "Translation failed",
      description: errorMessage,
      variant: "destructive",
    });

    setIsLoading(false);
  };

  const handleTranslate = () => {
    if (!sourceText.trim()) {
      toast({
        title: "Enter text to translate",
        description: "Please enter some text in the source panel",
      });
      return;
    }

    if (sourceLanguage === targetLanguage) {
      toast({
        title: "Same language selected",
        description: "Please select different source and target languages",
      });
      return;
    }

    translateText(sourceText, sourceLanguage, targetLanguage);
  };

  const handleSwapLanguages = () => {
    if (sourceLanguage === "auto") return;

    setSourceLanguage(targetLanguage);
    setTargetLanguage(sourceLanguage);
    setSourceText(translatedText);
    setTranslatedText(sourceText);
  };

  const handleDeleteEntry = (id: string) => {
    deleteEntry(id);
    toast({
      title: "Entry deleted",
      description: "Translation removed from history",
    });
  };

  const handleDeleteMultiple = (ids: string[]) => {
    deleteMultiple(ids);
    toast({
      title: "Entries deleted",
      description: `${ids.length} translation${ids.length > 1 ? "s" : ""} deleted from history`,
    });
  };

  const handleToggleFavorite = (id: string) => {
    toggleFavorite(id);
  };

  const handleUpdateEntry = (id: string, updates: Partial<TranslationEntry>) => {
    updateEntry(id, updates);
  };

  const handleSelectEntry = (entry: TranslationEntry) => {
    setSourceLanguage(entry.sourceLanguage);
    setTargetLanguage(entry.targetLanguage);
    setSourceText(entry.sourceText);
    setTranslatedText(entry.translatedText);
  };

  const handleSendFeedback = () => {
    toast({
      title: "Feedback",
      description: "Feedback feature coming soon! Thank you for your interest.",
    });
  };

  // Auto-translate with debounce (1.5 seconds after user stops typing)
  useEffect(() => {
    // Don't auto-translate if empty, same languages, or already translating
    if (!sourceText.trim() || sourceLanguage === targetLanguage || isLoading) {
      return;
    }

    const timer = setTimeout(() => {
      // Double-check conditions before triggering (in case state changed during timeout)
      if (sourceText.trim() && sourceLanguage !== targetLanguage) {
        translateText(sourceText, sourceLanguage, targetLanguage);
      }
    }, 1500);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceText, sourceLanguage, targetLanguage]);

  return (
    <div className="min-h-screen bg-gradient-subtle">
      {/* Header */}
      <header className="border-b border-translator-border bg-translator-panel/50 backdrop-blur-sm safe-area-top">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="p-1.5 sm:p-2 bg-gradient-primary rounded-lg">
                <Zap className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl sm:text-2xl font-bold bg-gradient-primary bg-clip-text text-transparent">
                  TranslateLoo AI
                </h1>
                <p className="text-xs sm:text-sm text-muted-foreground hidden sm:block">
                  Powered by AI & n8n
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2 sm:gap-3">
              <HelpDialog />
              <ThemeToggle />
              <Button
                variant="outline"
                onClick={handleSendFeedback}
                className="hover:bg-gradient-primary hover:text-white transition-bounce touch-target"
                aria-label="Send feedback"
              >
                <MessageSquare className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">Send Feedback</span>
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-3 sm:px-4 py-4 sm:py-8">
        <div className="space-y-4 sm:space-y-8">
          {/* Language Selector */}
          <LanguageSelector
            sourceLanguage={sourceLanguage}
            targetLanguage={targetLanguage}
            onSourceLanguageChange={setSourceLanguage}
            onTargetLanguageChange={setTargetLanguage}
            onSwapLanguages={handleSwapLanguages}
          />

          {/* Translation Panels */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
            <TranslationPanel
              value={sourceText}
              onChange={setSourceText}
              placeholder="Enter text to translate..."
              isInput={true}
              showVoiceInput={true}
              maxLength={5000}
            />

            <TranslationPanel
              value={translatedText}
              placeholder="Translation will appear here..."
              isLoading={isLoading}
            />
          </div>


          {/* Translate Button & Checkbox */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <div className="flex items-center space-x-2 bg-white/50 backdrop-blur-sm px-4 py-2 rounded-lg border border-translator-border">
              <Checkbox
                id="editable-mode"
                checked={isEditable}
                onCheckedChange={(checked) => setIsEditable(checked as boolean)}
              />
              <Label
                htmlFor="editable-mode"
                className="text-sm font-medium cursor-pointer select-none"
              >
                Improve
              </Label>
            </div>

            <Button
              onClick={handleTranslate}
              disabled={!sourceText.trim() || isLoading || sourceLanguage === targetLanguage}
              size="lg"
              className="w-full sm:w-auto bg-gradient-primary hover:opacity-90 text-white border-0 shadow-medium hover:shadow-strong transition-bounce px-6 sm:px-8 py-3 text-base sm:text-lg font-medium touch-target"
            >
              {isLoading ? (
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  Translating...
                </div>
              ) : (
                <>
                  <Zap className="h-5 w-5 mr-2" />
                  Translate
                </>
              )}
            </Button>
          </div>

          {/* History */}
          <TranslationHistory
            entries={translationEntries}
            onDeleteEntry={handleDeleteEntry}
            onDeleteMultiple={handleDeleteMultiple}
            onToggleFavorite={handleToggleFavorite}
            onUpdateEntry={handleUpdateEntry}
            onSelectEntry={handleSelectEntry}
            searchHistory={searchHistory}
            exportHistory={exportHistory}
            importHistory={importHistory}
          />
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-translator-border bg-translator-panel/30 mt-16">
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="text-center space-y-2">
            <p className="text-muted-foreground">AI-powered translations with seamless n8n integration</p>
            <p className="text-sm text-muted-foreground">Built with love using React, TypeScript & Tailwind CSS</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
